import React, { useState, useEffect } from 'react';
import supabase from '../../supabaseClient';
import { EditMealModal } from './EditMealModal';
import { DateFilter } from './DateFilter';
import StreakTracker from '../../StreakTracker';
import MealLogPanel from './MealLogPanel';
import { buildSyncedMealTimestamps, getEffectiveMealTimestamp } from '../../utils/mealDate';

interface MealLogsViewProps {
  selectedUserId: string;
  clientName: string;
}

export function MealLogsView({ selectedUserId, clientName }: MealLogsViewProps) {
  const [logs, setLogs] = useState<any[]>([]);
  const [editingMeal, setEditingMeal] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    if (selectedUserId) {
      fetchMealLogs();
    }
  }, [selectedUserId, dateFilter]);

  const fetchMealLogs = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('Mastersheet')
        .select('*')
        .eq('telegram_user_id', selectedUserId)
        .order('created_at', { ascending: false });

      // Apply date filtering
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate: Date;

        switch (dateFilter) {
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
          default:
            startDate = new Date(0);
        }

        query = query.gte('meal_time', startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching meal logs:', error);
        return;
      }

      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching meal logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (meal: any) => {
    setEditingMeal(meal);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (mealId: number) => {
    if (!window.confirm('Are you sure you want to delete this meal?')) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('Mastersheet')
        .delete()
        .eq('id_master', mealId);

      if (error) {
        console.error('Error deleting meal:', error);
        return;
      }

      fetchMealLogs();
    } catch (error) {
      console.error('Error deleting meal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async (updatedData: any) => {
    try {
      setLoading(true);

      const payload = { ...updatedData };
      if (updatedData.meal_time) {
        const synced = buildSyncedMealTimestamps(updatedData.meal_time);
        payload.meal_time = synced.meal_time;
        payload.created_at = synced.created_at;
      }

      const { error } = await supabase
        .from('Mastersheet')
        .update(payload)
        .eq('id_master', editingMeal.id_master);

      if (error) {
        console.error('Error updating meal:', error);
        return;
      }

      setIsEditModalOpen(false);
      setEditingMeal(null);
      fetchMealLogs();
    } catch (error) {
      console.error('Error updating meal:', error);
    } finally {
      setLoading(false);
    }
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingMeal(null);
  };

  return (
    <div className="space-y-6 pb-32 md:pb-12">
      {/* Client Header */}
      <div className="retro-card p-4 md:p-6">
        <div className="index-tab">
          <h2 className="text-xl font-bold text-text-dark">🍽️ Meal Logs</h2>
        </div>
        <div className="p-3 md:p-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-neon-blue to-neon-green rounded-full flex items-center justify-center text-white font-bold text-lg">
              {clientName.split(' ').map(n => n[0]).join('').toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-dark">{clientName}</h3>
              <p className="text-sm text-text-medium">Meal tracking and nutrition data</p>
            </div>
          </div>
        </div>
      </div>

      {/* Log new meal */}
      <MealLogPanel
        selectedUserId={selectedUserId}
        clientName={clientName}
        onMealLogged={fetchMealLogs}
      />

      {/* Date Filter */}
      <DateFilter 
        value={dateFilter} 
        onChange={setDateFilter} 
      />

      {/* Streak Tracker */}
      <StreakTracker 
        selectedUserId={selectedUserId} 
        clientName={clientName}
      />

      {/* Meal Logs Table */}
      <div className="retro-card">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-text-dark">Meal Logs</h3>
        </div>
        <div className="p-3 md:p-4">
          {loading ? (
            <div className="text-center py-8 text-text-medium">
              Loading meal logs...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-text-medium">
              No meals found for the selected time range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" role="table" aria-label="Meal logs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Meal
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Protein (g)
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Carbs (g)
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Fat (g)
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Calories
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Meal Time
                    </th>
                    <th className="px-2 md:px-4 py-2 text-left text-text-dark font-medium text-sm md:text-base">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const mealDate = getEffectiveMealTimestamp(log);
                    const isFalseEntry = !log.input_text || !mealDate;
                    const isWorkout = log.calories < 0;
                    return (
                    <tr key={log.id_master} className={`border-b border-gray-200 hover:bg-paper-cream ${isWorkout ? 'bg-purple-50' : ''}`}>
                      <td className="px-2 md:px-4 py-2 text-text-dark text-sm md:text-base max-w-xs truncate">
                        {isFalseEntry ? (
                          <span className="text-red-600 font-medium">False entry (photo attempt)</span>
                        ) : (
                          log.input_text
                        )}
                      </td>
                      <td className="px-2 md:px-4 py-2 text-text-dark text-sm md:text-base">
                        {log.protein_g}
                      </td>
                      <td className="px-2 md:px-4 py-2 text-text-dark text-sm md:text-base">
                        {log.carbs_g}
                      </td>
                      <td className="px-2 md:px-4 py-2 text-text-dark text-sm md:text-base">
                        {log.fat_g}
                      </td>
                      <td className="px-2 md:px-4 py-2 text-sm md:text-base">
                        {log.calories < 0 ? (
                          <span className="text-purple-600 font-semibold">
                            🏋️ {log.calories} (workout)
                          </span>
                        ) : (
                          <span className="text-text-dark">{log.calories}</span>
                        )}
                      </td>
                      <td className="px-2 md:px-4 py-2 text-text-dark text-sm md:text-base">
                        <span className="hidden md:inline">
                          {mealDate ? mealDate.toLocaleString() : '—'}
                        </span>
                        <span className="md:hidden">
                          {mealDate ? mealDate.toLocaleDateString() : '—'}
                        </span>
                        {isFalseEntry && (
                          <span className="ml-2 text-xs text-red-500">(ignored in streaks)</span>
                        )}
                      </td>
                      <td className="px-2 md:px-4 py-2">
                        <div className="flex flex-col md:flex-row space-y-1 md:space-y-0 md:space-x-2">
                          <button
                            onClick={() => handleEdit(log)}
                            className="neon-button text-xs px-2 py-1"
                            disabled={loading}
                            aria-label={`Edit meal: ${log.input_text}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(log.id_master)}
                            className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50"
                            disabled={loading}
                            aria-label={`Delete meal: ${log.input_text}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <EditMealModal
        meal={editingMeal}
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onSave={handleSaveEdit}
        loading={loading}
      />
    </div>
  );
} 